import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

import type { TotpSetupResponse, TotpStatus } from '@smartapply/shared';

import { PageShell } from '../components/layout/PageShell.js';
import { Stamp } from '../components/ui/Stamp.js';
import { SmartApplyApiError } from '../lib/api-client.js';
import {
  totpDisable,
  totpEnable,
  totpRegenerateRecovery,
  totpSetup,
  totpStatus,
} from '../lib/api-client.js';

type View = 'idle' | 'setup' | 'confirm' | 'success' | 'disable' | 'regenerate';

export function SecurityRoute() {
  const [status, setStatus] = useState<TotpStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refreshStatus() {
    try {
      const s = await totpStatus();
      setStatus(s);
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao consultar status.');
    }
  }

  useEffect(() => {
    void refreshStatus();
  }, []);

  return (
    <PageShell>
      <header className="mb-10">
        <p className="mb-4 inline-block">
          <Stamp>Segurança</Stamp>
        </p>
        <h1 className="font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[40px]">
          Autenticação em 2 fatores
          <span className="text-[var(--accent-saffron)]">.</span>
        </h1>
        <p className="mt-3 max-w-2xl font-sans text-sm text-[var(--ink-secondary)]">
          Adicione um código gerado por app (Google Authenticator, Authy, 1Password) ao seu
          login. Sem isso, qualquer um com sua senha entra. Com isso, precisa também do seu
          celular.
        </p>
      </header>

      {error && (
        <p className="mb-6 font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
      )}

      {status === null && !error && (
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          carregando...
        </p>
      )}

      {status && (
        <TotpManager status={status} onChange={() => void refreshStatus()} />
      )}
    </PageShell>
  );
}

function TotpManager({ status, onChange }: { status: TotpStatus; onChange: () => void }) {
  const [view, setView] = useState<View>('idle');
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function startSetup() {
    setError(null);
    setBusy(true);
    try {
      const data = await totpSetup();
      setSetupData(data);
      const dataUrl = await QRCode.toDataURL(data.otpauthUrl, {
        width: 240,
        margin: 1,
        color: { dark: '#1b1f23', light: '#f4efe6' },
      });
      setQrDataUrl(dataUrl);
      setView('setup');
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao iniciar setup.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmSetup(code: string) {
    if (!setupData) return;
    setError(null);
    setBusy(true);
    try {
      await totpEnable({
        secretBase32: setupData.secretBase32,
        recoveryCodes: setupData.recoveryCodes,
        code,
      });
      setView('success');
      onChange();
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Código inválido.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDisable(password: string) {
    setError(null);
    setBusy(true);
    try {
      await totpDisable(password);
      setView('idle');
      setSetupData(null);
      setQrDataUrl(null);
      onChange();
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao desabilitar.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRegenerate(password: string) {
    setError(null);
    setBusy(true);
    try {
      const { recoveryCodes } = await totpRegenerateRecovery(password);
      setSetupData({
        secretBase32: '',
        otpauthUrl: '',
        recoveryCodes,
      });
      setView('success');
      onChange();
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao gerar.');
    } finally {
      setBusy(false);
    }
  }

  if (status.enabled && view === 'idle') {
    return (
      <article className="card-paper rounded-sm px-7 py-6" style={{ borderLeft: '3px solid var(--accent-forest)' }}>
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-forest)]">
          ativado
        </p>
        <p className="mt-2 font-serif text-xl text-[var(--ink-primary)]">
          2FA está ligado nesta conta.
        </p>
        <dl className="mt-4 grid gap-2 sm:grid-cols-2">
          <div>
            <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              ativado em
            </dt>
            <dd className="mt-0.5 font-mono text-xs tabular-nums text-[var(--ink-secondary)]">
              {status.enabledAt
                ? new Date(status.enabledAt).toLocaleString('pt-BR')
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              recovery codes restantes
            </dt>
            <dd
              className="mt-0.5 font-mono text-xs tabular-nums"
              style={{
                color:
                  status.recoveryCodesRemaining < 3
                    ? 'var(--accent-crimson)'
                    : 'var(--ink-secondary)',
              }}
            >
              {status.recoveryCodesRemaining} / 10
            </dd>
          </div>
        </dl>

        <div className="mt-6 flex flex-wrap gap-4">
          <button
            type="button"
            onClick={() => setView('regenerate')}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-saffron)] hover:underline"
          >
            gerar novos recovery codes
          </button>
          <button
            type="button"
            onClick={() => setView('disable')}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-crimson)] hover:underline"
          >
            desabilitar 2FA
          </button>
        </div>
      </article>
    );
  }

  if (!status.enabled && view === 'idle') {
    return (
      <article className="card-paper rounded-sm px-7 py-6" style={{ borderLeft: '3px solid var(--ink-tertiary)' }}>
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          desativado
        </p>
        <p className="mt-2 font-serif text-xl text-[var(--ink-primary)]">
          Habilite 2FA pra proteger sua conta.
        </p>
        <p className="mt-3 font-sans text-sm text-[var(--ink-secondary)]">
          Você vai precisar de um app autenticador no celular (Google Authenticator, Authy,
          1Password). Vamos gerar um QR code, você escaneia, e a partir do próximo login
          pediremos um código de 6 dígitos.
        </p>
        {error && (
          <p className="mt-4 font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
        )}
        <button
          type="button"
          onClick={() => void startSetup()}
          disabled={busy}
          className="mt-6 rounded-sm bg-[var(--ink-primary)] px-5 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {busy ? 'Gerando...' : 'Habilitar 2FA'}
        </button>
      </article>
    );
  }

  if (view === 'setup' && setupData && qrDataUrl) {
    return (
      <SetupWizard
        qrDataUrl={qrDataUrl}
        secretBase32={setupData.secretBase32}
        recoveryCodes={setupData.recoveryCodes}
        onConfirm={confirmSetup}
        onCancel={() => {
          setView('idle');
          setSetupData(null);
          setQrDataUrl(null);
        }}
        busy={busy}
        error={error}
      />
    );
  }

  if (view === 'success' && setupData) {
    return (
      <SuccessPanel
        recoveryCodes={setupData.recoveryCodes}
        onClose={() => {
          setView('idle');
          setSetupData(null);
          setQrDataUrl(null);
          onChange();
        }}
      />
    );
  }

  if (view === 'disable') {
    return (
      <PasswordPanel
        title="Desabilitar 2FA"
        description="Confirme sua senha pra desativar o 2FA. Depois disso, login volta a usar só e-mail+senha."
        actionLabel="Desabilitar"
        actionColor="var(--accent-crimson)"
        onSubmit={handleDisable}
        onCancel={() => setView('idle')}
        busy={busy}
        error={error}
      />
    );
  }

  if (view === 'regenerate') {
    return (
      <PasswordPanel
        title="Gerar novos recovery codes"
        description="Confirme sua senha. Os 10 códigos atuais serão invalidados — você vai receber 10 novos. Salve em um gerenciador de senhas."
        actionLabel="Gerar novos"
        actionColor="var(--accent-saffron)"
        onSubmit={handleRegenerate}
        onCancel={() => setView('idle')}
        busy={busy}
        error={error}
      />
    );
  }

  return null;
}

function SetupWizard({
  qrDataUrl,
  secretBase32,
  recoveryCodes,
  onConfirm,
  onCancel,
  busy,
  error,
}: {
  qrDataUrl: string;
  secretBase32: string;
  recoveryCodes: string[];
  onConfirm: (code: string) => Promise<void>;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [code, setCode] = useState('');
  const [showCodes, setShowCodes] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onConfirm(code.trim());
  }

  return (
    <article className="card-paper rounded-sm px-7 py-6" style={{ borderLeft: '3px solid var(--accent-saffron)' }}>
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-saffron)]">
        passo 1 de 2
      </p>
      <h2 className="mt-2 font-serif text-2xl text-[var(--ink-primary)]">Escaneie o QR</h2>
      <p className="mt-2 font-sans text-sm text-[var(--ink-secondary)]">
        Abra seu app autenticador e adicione uma conta nova. Aponte a câmera pro QR abaixo.
      </p>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div>
          <img
            src={qrDataUrl}
            alt="QR code 2FA"
            width={240}
            height={240}
            className="rounded-sm border border-[var(--border-subtle)] bg-[var(--bg-paper)]"
          />
          <button
            type="button"
            onClick={() => setShowSecret((v) => !v)}
            className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
          >
            {showSecret ? 'esconder secret' : 'não consigo escanear, mostrar secret'}
          </button>
          {showSecret && (
            <p className="mt-2 break-all rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper-recessed)] p-2 font-mono text-xs tabular-nums text-[var(--ink-secondary)]">
              {secretBase32}
            </p>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              recovery codes (10)
            </p>
            <p className="mt-1 font-sans text-xs italic text-[var(--ink-tertiary)]">
              Salve em um gerenciador de senhas. Cada um pode ser usado UMA vez se você perder
              o celular.
            </p>
            <button
              type="button"
              onClick={() => setShowCodes((v) => !v)}
              className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent-saffron)] hover:underline"
            >
              {showCodes ? 'esconder' : 'mostrar codes'}
            </button>
            {showCodes && (
              <ul className="mt-2 grid grid-cols-2 gap-1 rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper-recessed)] p-2">
                {recoveryCodes.map((c) => (
                  <li key={c} className="font-mono text-xs tabular-nums text-[var(--ink-secondary)]">
                    {c}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(recoveryCodes.join('\n'))}
              className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
            >
              copiar todos
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 border-t border-[var(--border-subtle)] pt-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-saffron)]">
          passo 2 de 2
        </p>
        <h3 className="mt-2 font-serif text-xl text-[var(--ink-primary)]">
          Confirme com o código atual
        </h3>
        <p className="mt-1 font-sans text-sm text-[var(--ink-secondary)]">
          Digite o código de 6 dígitos que aparece no app pra confirmar que tudo bateu.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <input
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            maxLength={6}
            autoFocus
            className="w-32 rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-base tabular-nums text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
          />
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            className="rounded-sm bg-[var(--ink-primary)] px-5 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {busy ? 'Confirmando...' : 'Confirmar e ativar'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
          >
            cancelar
          </button>
        </div>
        {error && (
          <p className="mt-3 font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
        )}
      </form>
    </article>
  );
}

function SuccessPanel({
  recoveryCodes,
  onClose,
}: {
  recoveryCodes: string[];
  onClose: () => void;
}) {
  return (
    <article className="card-paper rounded-sm px-7 py-6" style={{ borderLeft: '3px solid var(--accent-forest)' }}>
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-forest)]">
        sucesso
      </p>
      <h2 className="mt-2 font-serif text-2xl text-[var(--ink-primary)]">2FA ativado</h2>
      <p className="mt-2 font-sans text-sm text-[var(--ink-secondary)]">
        Da próxima vez que você logar, vamos pedir o código de 6 dígitos. Salve os recovery
        codes abaixo num gerenciador de senhas — eles são sua única saída se perder o
        celular.
      </p>
      <ul className="mt-5 grid grid-cols-2 gap-1 rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper-recessed)] p-3 sm:grid-cols-3">
        {recoveryCodes.map((c) => (
          <li key={c} className="font-mono text-xs tabular-nums text-[var(--ink-secondary)]">
            {c}
          </li>
        ))}
      </ul>
      <div className="mt-5 flex flex-wrap gap-4">
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(recoveryCodes.join('\n'))}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-saffron)] hover:underline"
        >
          copiar todos
        </button>
        <button
          type="button"
          onClick={onClose}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
        >
          fechar — já salvei
        </button>
      </div>
    </article>
  );
}

function PasswordPanel({
  title,
  description,
  actionLabel,
  actionColor,
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  title: string;
  description: string;
  actionLabel: string;
  actionColor: string;
  onSubmit: (password: string) => Promise<void>;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [password, setPassword] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSubmit(password);
  }

  return (
    <article className="card-paper rounded-sm px-7 py-6" style={{ borderLeft: `3px solid ${actionColor}` }}>
      <h2 className="font-serif text-2xl text-[var(--ink-primary)]">{title}</h2>
      <p className="mt-2 font-sans text-sm text-[var(--ink-secondary)]">{description}</p>
      <form onSubmit={handleSubmit} className="mt-5 flex flex-wrap items-end gap-4">
        <div>
          <label
            htmlFor="confirm-password"
            className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
          >
            Senha
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
            className="mt-2 w-72 rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !password}
          className="rounded-sm px-5 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:opacity-40"
          style={{ backgroundColor: actionColor }}
        >
          {busy ? '...' : actionLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
        >
          cancelar
        </button>
      </form>
      {error && <p className="mt-3 font-sans text-sm text-[var(--accent-crimson)]">{error}</p>}
    </article>
  );
}
